/**
 * MED-1350: one rule for the end time of every start and end pair.
 *
 * Every form that has a start and an end calls these helpers, so the rule
 * cannot drift between forms:
 *   - nextEndTime: the end after the start is set or changed.
 *   - defaultSessionLengthMinutes: the usual length of a session row.
 *   - endTimeError: the inline error for the end field. A form blocks Save
 *     while it returns a message.
 */
import { CHECKIN_DURATION_MINUTES, isCheckinOneOnOneType, type OneOnOneType } from '../types';

/** Used when a 1:1 row and its cohort give no length. */
export const FALLBACK_ONE_ON_ONE_LENGTH_MINUTES = 60;

/** The time a date pick gives a start field that has no time yet. */
export const DEFAULT_START_TIME_ON_DATE_PICK = '09:00';

const MINUTE_MS = 60_000;

function positiveMinutes(value: number | null | undefined): number | null {
  return typeof value === 'number' && value > 0 ? value : null;
}

/**
 * The end after the start changes.
 *
 * - No new start: the end stays as it is.
 * - A valid end exists (after the old start): keep its length, so
 *   end = new start + (old end - old start). A length the user set by hand
 *   is kept.
 * - Else, with a default length: end = new start + the default length.
 * - Else: the end stays as it is. endTimeError then asks for an end.
 */
export function nextEndTime({
  previousStart,
  previousEnd,
  nextStart,
  defaultLengthMinutes,
}: {
  previousStart: Date | null;
  previousEnd: Date | null;
  nextStart: Date | null;
  defaultLengthMinutes: number | null | undefined;
}): Date | null {
  if (!nextStart) return previousEnd;
  if (previousStart && previousEnd) {
    const lengthMs = previousEnd.getTime() - previousStart.getTime();
    if (lengthMs > 0) return new Date(nextStart.getTime() + lengthMs);
  }
  const minutes = positiveMinutes(defaultLengthMinutes);
  if (minutes) return new Date(nextStart.getTime() + minutes * MINUTE_MS);
  return previousEnd;
}

/**
 * MED-1360: the length an empty end field assumes when the form knows no
 * usual length. The API uses the same 60 minutes for a cohort event.
 */
export const FALLBACK_END_LENGTH_MINUTES = 60;

/** "HH:mm" in local time, the format the date-time field reads. */
function toTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * MED-1360: what an empty end field shows next to a start. The calendar opens
 * on the start's month, and the time box shows the start plus the length. A
 * date-only pick gives that time. When the start plus the length passes
 * midnight, a pick on the start's day gives the next day (endOnDatePick), so
 * an 11:30 PM start ends at 12:30 AM the next day, not before the start.
 * With no start the field keeps its defaults.
 */
export function endFieldDefaults(
  start: Date | null,
  lengthMinutes: number | null | undefined,
): { defaultDate?: Date; defaultTimeOnDatePick?: string; endOnDatePick?: (picked: Date) => Date } {
  if (!start) return {};
  const minutes = positiveMinutes(lengthMinutes) ?? FALLBACK_END_LENGTH_MINUTES;
  const end = new Date(start.getTime() + minutes * MINUTE_MS);
  return {
    defaultDate: start,
    defaultTimeOnDatePick: toTimeString(end),
    endOnDatePick: (picked: Date) => {
      const sameDayAsStart =
        picked.getFullYear() === start.getFullYear() &&
        picked.getMonth() === start.getMonth() &&
        picked.getDate() === start.getDate();
      if (sameDayAsStart) return new Date(end.getTime());
      const result = new Date(picked.getTime());
      result.setHours(end.getHours(), end.getMinutes(), 0, 0);
      return result;
    },
  };
}

/** MED-1360: the one text for an end at or before its start, in every form. */
export const END_AFTER_START_ERROR = 'The end must be after the start.';

/** The inline error for an end field, or undefined when the pair is valid. */
export function endTimeError(start: Date | null, end: Date | null): string | undefined {
  if (!start) return undefined;
  if (!end) return 'Enter an end time.';
  if (end.getTime() <= start.getTime()) return END_AFTER_START_ERROR;
  return undefined;
}

export interface SessionLengthModality {
  id?: string;
  modality_type: string;
  session_duration_minutes: number | null;
}

/**
 * The usual length of a session row, in minutes, or null when none is known.
 * Never invents a length, except the documented 1:1 fallback.
 *
 * - one_on_one_session: a check-in is CHECKIN_DURATION_MINUTES; else the row's
 *   own length; else the cohort's 1:1 modality; else
 *   FALLBACK_ONE_ON_ONE_LENGTH_MINUTES.
 * - group_session: the row's own length; else the row's own modality; else the
 *   cohort's group_coaching modality. Most group rows have no length and no
 *   modality_id, so the cohort fallback matters.
 * - workshop_session: the row's own length; else the workshop definition's
 *   length, when the caller passes it.
 * - Every other type (huddle, non_coaching_one_on_one): the row's own length.
 */
export function defaultSessionLengthMinutes(
  session: {
    session_type?: string | null;
    one_on_one_type?: OneOnOneType | null;
    duration_minutes?: number | null;
    modality_id?: string | null;
  },
  options: {
    cohortModalities?: SessionLengthModality[] | null;
    workshopLengthMinutes?: number | null;
  } = {},
): number | null {
  const rowMinutes = positiveMinutes(session.duration_minutes);
  const modalities = options.cohortModalities ?? [];
  const modalityMinutes = (type: string): number | null => {
    const own = session.modality_id ? modalities.find((m) => m.id === session.modality_id) : undefined;
    if (own && own.modality_type === type) {
      const minutes = positiveMinutes(own.session_duration_minutes);
      if (minutes) return minutes;
    }
    return positiveMinutes(modalities.find((m) => m.modality_type === type)?.session_duration_minutes);
  };

  switch (session.session_type) {
    case 'one_on_one_session':
      if (isCheckinOneOnOneType(session.one_on_one_type)) return CHECKIN_DURATION_MINUTES;
      return rowMinutes ?? modalityMinutes('one_on_one') ?? FALLBACK_ONE_ON_ONE_LENGTH_MINUTES;
    case 'group_session':
      return rowMinutes ?? modalityMinutes('group_coaching');
    case 'workshop_session':
      return rowMinutes ?? positiveMinutes(options.workshopLengthMinutes);
    default:
      return rowMinutes;
  }
}
